import type { CogoNavigationGuard } from "../core/types"

export interface GuardResult {
  allow: boolean
  redirect?: string
}

const DEFAULT_TIMEOUT = 5000

export class NavigationGuardRunner {
  constructor(
    private guards: CogoNavigationGuard[],
    private executeFlow: (flowName: string, context: Record<string, unknown>) => Promise<void>,
    private getState: (path: string) => unknown,
  ) {}

  async check(targetRoute: string, currentRoute: string): Promise<GuardResult> {
    const matching = this.guards.filter((g) =>
      NavigationGuardRunner.matchPattern(g.pattern, targetRoute),
    )

    if (matching.length === 0) {
      return { allow: true }
    }

    for (const guard of matching) {
      const timeout = guard.timeout ?? DEFAULT_TIMEOUT
      const context = { targetRoute, currentRoute }

      try {
        await Promise.race([
          this.executeFlow(guard.flow, context),
          rejectAfter(timeout),
        ])
      } catch {
        // Timeout — deny
        return { allow: false }
      }

      const allow = this.getState("_guard.allow")
      if (!allow) {
        const redirect = this.getState("_guard.redirect") as string | undefined
        return { allow: false, ...(redirect != null ? { redirect } : {}) }
      }
    }

    return { allow: true }
  }

  static matchPattern(pattern: string, route: string): boolean {
    if (!pattern || !route) return false

    if (pattern === route) {
      return true
    }

    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -2) // remove "/*"
      return route.startsWith(prefix + "/")
    }

    return false
  }
}

function rejectAfter(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error("Guard timeout")), ms)
  })
}
